// Third party
import React from 'react';
import ReactDOMServer from 'react-dom/server';

// Namespace
import * as ProposalNS from '../Proposal';

// Components
import HoldingIcon, { Props as HoldingIconProps } from '../../../views/components/common/HoldingIcon';

// Utils
import { formatMessageWithLocale, formatPercentage } from '../../../utils/i18n';

// Constant and types
import { PROPOSAL_ORIENTATION } from '../utils/constants';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import ComparisonItem from '@/types/comparison-item';
import ProposalOrientation from '@/types/proposal-orientation';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';

type BenchmarksBreakdownDisclaimerHolder = {
  title: string;
  text: string;
};

class ProposalBenchmarksBreakdownDisclaimersSectionBuilder {
  newSections: ProposalNS.ProposalTemplateSectionElement[] = [];

  proposalItems: ComparisonItem[];

  orientation: ProposalOrientation;

  locale: Locale;

  benchmarksBreakdownDisclaimers: LocalizedString;

  constructor(proposalItems: ComparisonItem[], benchmarksBreakdownDisclaimers: LocalizedString, orientation: ProposalOrientation, locale: Locale) {
    this.proposalItems = proposalItems;
    this.benchmarksBreakdownDisclaimers = benchmarksBreakdownDisclaimers;
    this.orientation = orientation;
    this.locale = locale;
  }

  splitIntoPages = (): BenchmarksBreakdownDisclaimerHolder[][] => {
    const CHARS_PER_LINE = this.orientation === PROPOSAL_ORIENTATION.PORTRAIT ? 140 : 225;
    const PIXELS_PER_PAGE = this.orientation === PROPOSAL_ORIENTATION.PORTRAIT ? 1100 : 800; // to adjust

    const PIXELS_PER_LINE = 20;
    const PIXELS_PER_HEADER = 22;

    const pages: BenchmarksBreakdownDisclaimerHolder[][] = [[]]; // Initialize pages list with an empty page

    // Keep track of current page index
    let currentPageIndex: number = 0;
    // Keep track of the % of remaining space on the current page
    let remainingAllocation: number = 100;

    for (const item of this.proposalItems) {
      if (item.type === COMPARISON_ITEM_TYPES.BENCHMARK) {
        const holdingIcon: string = ReactDOMServer.renderToString(
          React.createElement<HoldingIconProps>(HoldingIcon, {
            type: item.type,
            color: item.color,
          }),
        );

        const bechmarkName = `${holdingIcon}\xa0${item.codes ? item.codes[0] : item.name[this.locale] || ''}`;

        const strs: string[] = [];
        for (const holding of item.holdings) {
          const name = holding.name ? holding.name[this.locale] : undefined;
          const weight = holding?.weight != null ? formatPercentage(holding.weight, 1, this.locale) : undefined;
          if (name != null && weight != null) {
            strs.push(`${weight} ${name}`);
          }
        }

        const breakdown = `\xa0${strs.join(',\xa0')}`;

        let bechmarkText = this.benchmarksBreakdownDisclaimers[this.locale] || '';
        bechmarkText = bechmarkText.replace('{{breakdown}}', breakdown);

        // Fits about 140 chars on a line, one line takes about 25px. Calculate space taken from those values.
        // Yes, this seems extremely arbitrary, and that's because it is - but this is probably the best we can do
        // given the resources available.

        const title = formatMessageWithLocale('Disclaimers.CustomDisclaimer.Title', this.locale, { items: bechmarkName });
        const text = bechmarkText;
        const paragraphsCount = text.match(/<p>/g)?.length || 1;
        const textSpace = (text.length / CHARS_PER_LINE) * PIXELS_PER_LINE;
        const pageSpace = ((textSpace + paragraphsCount * PIXELS_PER_LINE + PIXELS_PER_HEADER) / PIXELS_PER_PAGE) * 100;

        const currentAllocation = Math.min(pageSpace, 100);

        if (remainingAllocation < currentAllocation) {
          currentPageIndex++;
          remainingAllocation = 100;
          pages[currentPageIndex] = [];
        }

        pages[currentPageIndex].push({ title, text });
        remainingAllocation -= currentAllocation;
      }
    }

    if (pages[0].length === 0) {
      // we do not have a single disclaimer, do not build anything
      return [];
    }

    return pages;
  };

  build = () => {
    const pages = this.splitIntoPages();

    if (pages.length === 0) {
      return [];
    }

    this.newSections = pages.map(
      (page, pageIndex) =>
        ({
          type: 'SECTION',
          sectionType: 'INSTRUMENT_CUSTOM_DISCLAIMERS',
          key: `INSTRUMENT_CUSTOM_DISCLAIMERS_${pageIndex}`,
          forced: true,
          buildRules: [],
          pageFraction: {
            Portrait: 1,
            Landscape: 1,
          },
          label: {
            'en-CA': 'Benchmarks Breakdown Disclaimers',
            'fr-CA': 'Avis de non-responsabilité ',
          },
          sidebarControl: 'HIDE',
          showTitle: false,
          showItemNamesInDisclaimers: true,
          layout: {
            type: 'LAYOUT',
            layout: {
              type: 'GRID',
              columns: 1,
            },
            elements: [
              {
                type: 'DATA',
                dataView: {
                  type: 'HTML',
                },
                DAO: {
                  type: ProposalNS.DAOType.HTMLContent,
                  htmlContentDAOOptions: {
                    html: [
                      {
                        className: 'disclaimers-page-content',
                        content: {
                          'en-CA': `
                          <div class="page-container">
                            <div class="content">
                              <h2 class="main-heading">Disclaimers</h2>
                            </div>
                          </div>
                          `,
                          'fr-CA': `
                          <div class="page-container">
                            <div class="content">
                              <h2 class="main-heading">Mentions juridiques</h2>
                            </div>
                          </div>
                          `,
                        },
                      },
                    ],
                  },
                },
              },
              {
                type: 'DATA',
                dataView: {
                  type: 'HTML',
                },
                DAO: {
                  type: ProposalNS.DAOType.HTMLContent,
                  htmlContentDAOOptions: {
                    html: page.map(content => {
                      const formatedContent = `
                    <div class="disclaimer-title">
                      <h2>${content.title}</h2>
                    </div>
                    <div class="disclaimer-content">
                      <p>${content.text}</p>
                    </div>
                  `;

                      return {
                        content: {
                          // content is generated in the right local, we simply pass in the same value.
                          'en-CA': formatedContent,
                          'fr-CA': formatedContent,
                        },
                      };
                    }),
                  },
                },
              },
            ],
          },
        }) as ProposalNS.ProposalTemplateSectionElement,
    );
  };
}

export const buildBenchmarksBreakdownDisclaimers = (
  proposalItems: ComparisonItem[],
  benchmarksBreakdownDisclaimers: LocalizedString,
  orientation: ProposalOrientation,
  locale: Locale,
): ProposalNS.ProposalTemplateSectionElement[] => {
  const builder = new ProposalBenchmarksBreakdownDisclaimersSectionBuilder(proposalItems, benchmarksBreakdownDisclaimers, orientation, locale);
  builder.build();

  return builder.newSections;
};
